import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useDecision } from '@optimizely/react-sdk';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Grid } from '@mui/material';
import { mobileStartBreakpoint } from '@/materialUi/theme';
import { useBuilderRef } from '../hooks/usePizzaOptions';
import BuilderSteps from '../BuilderSteps';
import PizzaBuilderDesktop from '../PizzaBuilder/PizzaBuilderDesktop';
import PizzaBuilderDesktopExpanded from '../PizzaBuilder/PizzaBuilderDesktopExpanded';
import { glutenFreeDetails } from '../PizzaBuilder/GlutenFreeDetails/GlutenFreeDetails';
import { getGlobalId } from '@/cart/helpers/getGlobalId';
import { setPizzaId } from '../../../../optimizely/utils/attributeHelpers';
import { clearPizza, selectors } from '../slice/pizza.slice';
import useOutOfStockModal from '../hooks/useOutOfStockModal';
import { openModal } from '@/localization/actions';
import Routes from '../../../router/routes';
import usePizzaValidator from '../hooks/usePizzaValidator';
import { NavBarMobileProps } from '../NavBarMobile/NavBarMobile';
import { updatePanCrustToppingModalShown } from '../slice/modal/modal.slice';
import PizzaBuilderContainerSkeleton from '@/builders/SkeletonLoaders/PizzaBuilderContainer.skeleton';
import { DealRecipe } from '@/builders/deals/slice/dealTypes';
import AddToCartFooter from '@/common/AddToCartFooter';
import CenteredContainer from '@/common/CenteredContainer';
import CartTotalPrice from '../CartTotalPrice';
import { YUM_GLUTEN_FREE_CRUST_ID } from '@/builders/pizza/constants';

const useStyles = makeStyles((theme) => ({
  addToCartLegacy: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down(mobileStartBreakpoint)]: {
      width: '100%'
    },
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      width: '226px'
    }
  },
  addToCart: {
    flex: 1,
    [theme.breakpoints.up(mobileStartBreakpoint)]: {
      flex: '0 0 276px'
    }
  }
}));

export interface ContainerProps extends Pick<NavBarMobileProps, 'onExit'> {
  isLocalized: boolean;
  loading: boolean;
  AddToCartButton: JSX.Element;
  recipes?: DealRecipe[];
  isEditFlow?: boolean;
}

type Container = React.ComponentType<ContainerProps>;
const PizzaBuilderContainer: Container = ({
  recipes,
  isLocalized,
  loading,
  AddToCartButton,
  isEditFlow
}: ContainerProps): JSX.Element => {
  const router = useRouter();
  const classes = useStyles();
  const dispatch = useDispatch();
  const builderRef = useBuilderRef();
  const modalDetails = useOutOfStockModal();
  const hasPizzaOptions = useSelector(selectors.hasPizzaOptions);
  const summary = useSelector(selectors.selectPizzaSummaryOption);
  const pizzaId = getGlobalId(useSelector(selectors.selectPizzaId));
  const showsQuantityAndAddToCartButton = usePizzaValidator();
  const crustId = useSelector(selectors.selectPizzaCrustGlobalId);
  const [useExpandedDefaultDecision] = useDecision('cb-tavern_national_launch');
  const [{ enabled: isAddToCartButtonRefactorEnabled }] = useDecision('fr-dtg-1699-quantity-picker-cta-placement');

  const [step, setStep] = useState<BuilderSteps>(BuilderSteps.MAIN);

  const glutenFree = summary?.glutenFree || crustId === YUM_GLUTEN_FREE_CRUST_ID || false;

  useEffect(
    () => () => {
      dispatch(clearPizza());
      dispatch(updatePanCrustToppingModalShown(false));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isLocalized) {
      router.push(Routes.MENU.PIZZA);
    }
  }, [isLocalized, router]);

  useEffect(() => {
    if (!loading && hasPizzaOptions && modalDetails) dispatch(openModal(modalDetails));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, dispatch, hasPizzaOptions]);

  // Opens a warning modal for glutenFree crusts
  useEffect(() => {
    if (glutenFree && pizzaId) {
      dispatch(openModal(glutenFreeDetails(pizzaId)));
    }
    setPizzaId(pizzaId);
  }, [glutenFree, pizzaId, dispatch]);

  if (loading || !hasPizzaOptions) {
    return <PizzaBuilderContainerSkeleton />;
  }

  const PizzaBuilderComponent = useExpandedDefaultDecision.enabled ? PizzaBuilderDesktopExpanded : PizzaBuilderDesktop;

  const commonProps = {
    step,
    setStep,
    currentPath: router.asPath
  };

  return (
    <div data-testid="pizza-builder-container" ref={builderRef}>
      <PizzaBuilderComponent {...commonProps} recipes={recipes} isEditFlow={isEditFlow} />
      {showsQuantityAndAddToCartButton && (
        <AddToCartFooter>
          <CenteredContainer>
            {isAddToCartButtonRefactorEnabled ? (
              <Box display="flex" alignItems="center">
                <CartTotalPrice />
                <Box className={classes.addToCart}>{AddToCartButton}</Box>
              </Box>
            ) : (
              <Grid container wrap="nowrap" direction="row" justifyContent="space-between" alignItems="center">
                <Grid item className={classes.addToCartLegacy}>
                  <CartTotalPrice />
                  {AddToCartButton}
                </Grid>
              </Grid>
            )}
          </CenteredContainer>
        </AddToCartFooter>
      )}
    </div>
  );
};

export default PizzaBuilderContainer;
